'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { ShieldCheck, SlidersHorizontal } from 'lucide-react';
import { COOKIE_CONSENT_CHANGED_EVENT, isPostHogEnabledForPathname } from '@/lib/analytics/posthog-paths';
import { OPEN_PRIVACY_SETTINGS_EVENT, saveCompanyAnalyticsConsent, useCompanyAnalyticsConsent } from '@/lib/privacy/company-analytics-consent';

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
  }
}

const readStoredConsent = (): string | null => {
  try {
    return localStorage.getItem('cookieConsent');
  } catch {
    return null;
  }
};

const updateAnalyticsConsent = (granted: boolean): void => {
  try {
    localStorage.setItem('cookieConsent', granted ? 'accepted' : 'rejected');
  } catch {
    // The site remains usable if browser storage is unavailable.
  }
  window.dispatchEvent(new Event(COOKIE_CONSENT_CHANGED_EVENT));
  window.gtag?.('consent', 'update', {
    analytics_storage: granted ? 'granted' : 'denied', ad_storage: 'denied',
    ad_user_data: 'denied', ad_personalization: 'denied',
  });
};

export const CookieBanner = () => {
  const pathname = usePathname();
  const consent = useCompanyAnalyticsConsent();
  const [showBanner, setShowBanner] = useState(false);
  const [showOptions, setShowOptions] = useState(false);
  const [analyticsEnabled, setAnalyticsEnabled] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isPostHogEnabledForPathname(pathname || '/')) {
      setShowBanner(false);
      return;
    }
    // An unconfigured collector cannot persist a receipt; the browser choice
    // still applies to GA/PostHog and must survive a page reload.
    if (consent.loaded && (!readStoredConsent() || (consent.available && !consent.decided))) {
      setShowBanner(true);
      setAnalyticsEnabled(readStoredConsent() === 'accepted' && (!consent.available || consent.accepted));
    }
  }, [pathname, consent.loaded, consent.decided, consent.accepted, consent.available]);

  useEffect(() => {
    const open = () => {
      setAnalyticsEnabled(readStoredConsent() === 'accepted' && (!consent.available || consent.accepted));
      setShowOptions(true);
      setShowBanner(true);
    };
    window.addEventListener(OPEN_PRIVACY_SETTINGS_EVENT, open);
    return () => { window.removeEventListener(OPEN_PRIVACY_SETTINGS_EVENT, open); };
  }, [consent.accepted, consent.available]);

  const save = async (accepted: boolean) => {
    setSaving(true);
    setError(null);
    updateAnalyticsConsent(accepted);
    setAnalyticsEnabled(accepted);
    try {
      await saveCompanyAnalyticsConsent(accepted && consent.available);
      setShowBanner(false);
      setShowOptions(false);
    } catch {
      setError('Company analytics is paused in this tab. Your preference could not be saved; please retry.');
    } finally {
      setSaving(false);
    }
  };

  if (!showBanner) {
    return null;
  }

  return (
    <section aria-label="Privacy preferences" className="fixed inset-x-0 bottom-0 z-50 px-3 pb-3 sm:px-5 sm:pb-5">
      <div className="mx-auto max-w-6xl rounded-lg border border-gray-200 bg-white shadow-[0_16px_50px_rgba(15,23,42,0.22)]">
        <div className="flex flex-col gap-4 px-4 py-4 md:px-5 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex min-w-0 gap-3">
            <div className="mt-0.5 hidden h-9 w-9 shrink-0 items-center justify-center rounded-md bg-emerald-50 text-[var(--ra-green)] sm:flex">
              <ShieldCheck className="h-5 w-5" />
            </div>
            <div className="min-w-0 max-w-3xl">
              <h2 className="text-sm font-semibold text-[var(--ra-text-dark)]">
                Help us improve RatesAgent.AI
              </h2>
              <p className="mt-1 text-sm leading-5 text-[var(--ra-text-gray)]">
                Optional analytics cookies help us understand public-site usage.
                Rejecting will not affect the site. We also use
                privacy-minimized first-party analytics and an independent
                server-side search diagnostic journal as described in our{' '}
                <Link
                  href="/privacy/"
                  className="font-medium text-[var(--ra-green)] hover:underline"
                >
                  Privacy Policy
                </Link>
                .
              </p>

              {showOptions && (
                <div className="mt-3 grid gap-3 rounded-md border border-gray-200 bg-gray-50 p-3 sm:grid-cols-2">
                  <div className="rounded-md bg-white p-3">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          Necessary
                        </p>
                        <p className="mt-1 text-xs leading-5 text-gray-500">
                          Required for security, authentication, and core site
                          functionality.
                        </p>
                      </div>
                      <span className="rounded bg-gray-100 px-2 py-1 text-xs font-medium text-gray-600">
                        Always on
                      </span>
                    </div>
                  </div>

                  <label className="cursor-pointer rounded-md bg-white p-3">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          Analytics
                        </p>
                        <p className="mt-1 text-xs leading-5 text-gray-500">
                          Optional Google Analytics and PostHog, plus company
                          reports on views, clicks, and approximate visitor country.
                        </p>
                      </div>
                      <input
                        type="checkbox"
                        aria-label="Analytics"
                        disabled={saving}
                        checked={analyticsEnabled}
                        onChange={(event) => {
                          setAnalyticsEnabled(event.target.checked);
                        }}
                        className="mt-1 h-5 w-5 shrink-0 rounded border-gray-300 text-[var(--ra-green)] focus:ring-[var(--ra-green)]"
                      />
                    </div>
                  </label>
                </div>
              )}
              {error && (
                <p role="alert" className="mt-2 text-sm text-red-700">
                  <span>{error}</span>
                </p>
              )}
            </div>
          </div>

          <div className="flex shrink-0 flex-col gap-2 sm:flex-row lg:self-center">
            <button
              type="button"
              disabled={saving}
              onClick={() => {
                void save(true);
              }}
              className="inline-flex items-center justify-center rounded-md bg-[var(--ra-green)] px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-[var(--ra-green-hover)]"
            >
              <span>{saving ? 'Saving preferences…' : 'Accept analytics'}</span>
            </button>
            <button
              type="button"
              disabled={saving}
              onClick={() => {
                if (showOptions) {
                  void save(analyticsEnabled);
                } else {
                  setShowOptions(true);
                }
              }}
              className={
                showOptions
                  ? 'inline-flex items-center justify-center rounded-md border border-[var(--ra-green)] bg-white px-4 py-2 text-sm font-medium text-[var(--ra-green)] transition-colors hover:bg-emerald-50'
                  : 'inline-flex items-center justify-center gap-2 rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50'
              }
            >
              {!showOptions && <SlidersHorizontal className="h-4 w-4" />}
              <span>{showOptions ? 'Save preferences' : 'Manage'}</span>
            </button>
            <button
              type="button"
              disabled={saving}
              onClick={() => {
                void save(false);
              }}
              className="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
            >
              <span>Reject optional</span>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};
